import { SharedModule } from '@/app/shared/shared.module';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import {
  AppStartupModelResponseResult,
  KeyValueModel,
  KeyValueModelListResponseResult,
  LookupProxy,
  ProfessionLookupViewModel,
} from '@/app/proxy/proxies';
import { RegisterationSteps } from '@/app/auth/shared/auth-enums';
import { registrationProgress } from '@/app/auth/shared/registration-progress';
import { AuthService } from '@/app/auth/shared/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { PendingRegistrationProfile, UserService } from '@/app/core/services/user.service';

@Component({
  selector: 'auth-register-profession',
  imports: [SharedModule],
  providers: [LookupProxy],
  templateUrl: './profession.component.html',
  styleUrl: './profession.component.less',
})
export class ProfessionComponent implements OnInit {
  private readonly _mixpanelTracker = inject(MixpanelTrackerService);

  @Output() professionSelected = new EventEmitter<{
    entered: boolean;
    value: string | undefined;
    id: string | undefined;
  }>();
  /** Asks the parent to register. This step does not create the user. */
  @Output() registrationRequested = new EventEmitter<void>();

  options: ProfessionLookupViewModel[] = [];

  selectedOption: ProfessionLookupViewModel | undefined;
  submitting: boolean = false;
  browserTimezoneId: string | undefined;

  somethingWentWrong = false;
  errorMessage = '';

  constructor(
    private _lookupProxy: LookupProxy,
    private _authService: AuthService,
    private _userService: UserService
  ) {
    this._lookupProxy.lookup_AppStartupLookups().subscribe({
      next: (res: AppStartupModelResponseResult) => {
        this.options = res.result?.professions ?? [];
      },
      // The interceptor already toasts the failure; keep the step usable with no options.
      error: () => {
        this.options = [];
      },
    });
  }

  /** Parent keeps this true while register and sign-in are in flight. */
  @Input()
  set registrationPending(pending: boolean) {
    if (pending) {
      this.submitting = true;
    }
  }

  /** Parent reports a failed register or sign-in without leaving this step. */
  @Input()
  set registrationError(message: string) {
    if (!message) {
      this.somethingWentWrong = false;
      this.errorMessage = '';
      return;
    }

    this.somethingWentWrong = true;
    this.errorMessage = message;
    this.submitting = false;
  }

  get progressValues(): number[] {
    return registrationProgress(RegisterationSteps.PROFESSION, this._authService.getPhoneOtpRequired());
  }

  ngOnInit(): void {
    this.getBrowserTimezoneIdFromNebula().then((browserTimezoneId) => {
      if (browserTimezoneId) {
        this.browserTimezoneId = browserTimezoneId;
      }
    });
  }

  addOrRemoveProfession(option: ProfessionLookupViewModel) {
    if (this.selectedOption === option) {
      this.selectedOption = undefined;
    } else {
      this.selectedOption = option;
    }
  }

  private toOptionalGuid(value: string | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  }

  private async resolveBrowserTimezoneId(): Promise<string | undefined> {
    const existing = this.toOptionalGuid(this.browserTimezoneId);
    if (existing) {
      return existing;
    }

    const resolved = await this.getBrowserTimezoneIdFromNebula();
    if (resolved) {
      this.browserTimezoneId = resolved;
    }

    return this.toOptionalGuid(resolved);
  }

  async continueProfessionStep() {
    if (this.submitting) {
      return;
    }

    this.submitting = true;
    this.somethingWentWrong = false;
    this.errorMessage = '';

    await this.resolveBrowserTimezoneId();
    this.storePendingProfile(this.selectedOption?.key);

    // Mixpanel receives the profession id; `value` is the display name.
    this.professionSelected.emit({
      entered: !!this.selectedOption,
      value: this.selectedOption?.value || undefined,
      id: this.selectedOption?.key || undefined,
    });
    this._mixpanelTracker.setUserProfession(this.selectedOption?.key || undefined);
    this.registrationRequested.emit();
  }

  async getBrowserTimezoneIdFromNebula(): Promise<string | undefined> {
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const response = await firstValueFrom(
        this._lookupProxy.lookup_GetTimeZones().pipe(
          map((res: KeyValueModelListResponseResult) => {
            const timezone = res.result?.find(
              (tz: KeyValueModel) => tz.value === browserTimeZone
            );
            return timezone?.key;
          })
        )
      );

      return this.toOptionalGuid(response);
    } catch (error) {
      console.error('Failed to get timezone from Nebula:', error);
      return undefined;
    }
  }

  /**
   * Skip profession selection. Locale, timezone, and country are still stored
   * so they can be written to the profile after the account exists.
   */
  async skipProfession() {
    if (this.submitting) {
      return;
    }

    this.professionSelected.emit({
      entered: false,
      value: undefined,
      id: undefined,
    });

    this.submitting = true;
    this.somethingWentWrong = false;
    this.errorMessage = '';

    await this.resolveBrowserTimezoneId();
    this.storePendingProfile(undefined);
    this.registrationRequested.emit();
  }

  /** Country chosen on the phone step. A shared dial code is not enough to identify it. */
  private selectedRegistrationCountryId(): string | undefined {
    const countryId = this._authService.getRegisterModal()?.countryId?.trim();
    return countryId || undefined;
  }

  private storePendingProfile(professionId: string | undefined): void {
    const pending: PendingRegistrationProfile = {
      localeId: this._authService.getLanguage(),
      timeZoneId: this.toOptionalGuid(this.browserTimezoneId),
      countryId: this.selectedRegistrationCountryId(),
    };
    if (professionId) {
      pending.professionId = professionId;
    }
    this._userService.setPendingRegistrationProfile(pending);
  }
}
